import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import {
  TruckIcon,
  BoltIcon,
  CubeTransparentIcon,
} from "@heroicons/react/24/outline";

const gradientBg =
  "bg-gradient-to-b from-[#eef2ff] via-white to-white";

const serviceTexts = {
  godstransport: {
    title: "Godstransport",
    description:
      "Säker och effektiv transport av gods inom hela Norden.",
    details: `
Vår godstransporttjänst är utformad för företag som behöver pålitliga, punktliga och flexibla logistiska lösningar. 
Oavsett om det handlar om enstaka pallar, styckegods eller fulla lastbilslaster erbjuder vi optimala transportflöden med hög spårbarhet och transparens.

Vi arbetar med moderna fordon, erfarna chaufförer och smart ruttplanering, vilket ger kortare ledtider och hög leveransprecision. 
Alla transporter kan följas i realtid och vi erbjuder proaktiv kommunikation vid eventuella avvikelser.

Tjänsten passar särskilt bra för tillverkande företag, grossister, e handlare och verksamheter med återkommande flöden inom Norden.
    `,
    icon: TruckIcon,
  },

  expressleveranser: {
    title: "Expressleveranser",
    description:
      "Snabba leveranser när tiden är kritisk, med full kontroll.",
    details: `
När tiden är en avgörande faktor erbjuder våra expressleveranser den snabbaste och mest direkta vägen från avsändare till mottagare. 
Vi arbetar utan omlastningar, vilket minimerar risker och gör hela resan både snabbare och säkrare.

Med dedikerade fordon, tidsgaranti och direktleverans är detta tjänsten för dig som behöver akut leverans av reservdelar, medicinskt material, kritiska komponenter eller tidskänsliga dokument.
    `,
    icon: BoltIcon,
  },

  specialtransporter: {
    title: "Specialtransporter",
    description:
      "Skräddarsydda lösningar för känsligt, tungt eller skrymmande gods.",
    details: `
Våra specialtransporter är anpassade för gods som kräver mer än en standardlösning. 
Det kan handla om tunga maskiner, skrymmande konstruktioner, känslig utrustning eller transporter som kräver särskilda tillstånd och projektplanering.

Vi genomför en analys av rutt, lastning, tillstånd, eskortfordon och säkerhetsrutiner för att säkerställa ett helt friktionsfritt genomförande. 
Med lång erfarenhet inom speciallogistik hanterar vi allt från industriutrustning till tekniska system och prototyplaster.
    `,
    icon: CubeTransparentIcon,
  },
};

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = serviceTexts[serviceId];

  if (!service) {
    return (
      <main className={`${gradientBg} min-h-screen flex items-center justify-center`}>
        <h1 className="text-xl text-slate-700">Tjänsten hittades inte.</h1>
      </main>
    );
  }

  const Icon = service.icon;

  return (
    <main className={`${gradientBg} min-h-screen`}>

      {/* SEO META */}
      <Helmet>
        <title>{service.title} | Nordic Route</title>
        <meta name="description" content={service.description} />
        <link
          rel="canonical"
          href={`https://www.nordicroute.se/services/${serviceId}`}
        />

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "${service.title}",
            "description": "${service.description}",
            "provider": {
              "@type": "Organization",
              "name": "Nordic Route"
            },
            "areaServed": "Sweden, Norway, Denmark, Finland",
            "serviceType": "${service.title}",
            "url": "https://www.nordicroute.se/services/${serviceId}"
          }
          `}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          <div>
            <div className="inline-flex items-center justify-center rounded-2xl bg-indigo-100 p-4 shadow-sm">
              <Icon className="h-8 w-8 text-indigo-700" aria-hidden="true" />
            </div>

            <h1 className="mt-6 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
              {service.title}
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              {service.description}
            </p>

            <Link
              to="/contact"
              className="mt-10 inline-flex items-center rounded-full bg-indigo-600 px-8 py-3 text-sm font-medium text-white shadow-md hover:bg-indigo-700 transition"
            >
              Kontakta oss
            </Link>
          </div>

          {/* HERO CARD */}
          <div className="bg-white rounded-3xl shadow-lg p-8 border border-slate-100">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              Varför välja Nordic Route
            </h2>

            <ul className="space-y-4 text-slate-700 leading-relaxed">
              <li>• Hög leveransprecision inom hela Norden</li>
              <li>• Moderna fordon och erfarna chaufförer</li>
              <li>• Full spårbarhet och tydlig kommunikation</li>
              <li>• Skräddarsydda lösningar efter ditt behov</li>
            </ul>
          </div>

        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-6xl mx-auto px-6 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">

          {/* MAIN TEXT */}
          <article className="lg:col-span-2">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6">
              Om tjänsten
            </h2>

            <p className="text-slate-700 leading-relaxed text-lg whitespace-pre-line max-w-3xl">
              {service.details}
            </p>
          </article>

          {/* SIDEBAR */}
          <aside className="bg-slate-50 rounded-2xl p-6 h-fit">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wide">
              Relaterade tjänster
            </h3>

            <ul className="space-y-3 text-sm">
              <li>
                <Link className="text-indigo-700 hover:underline" to="/services/godstransport">
                  Godstransport
                </Link>
              </li>
              <li>
                <Link className="text-indigo-700 hover:underline" to="/services/expressleveranser">
                  Expressleveranser
                </Link>
              </li>
              <li>
                <Link className="text-indigo-700 hover:underline" to="/services/specialtransporter">
                  Specialtransporter
                </Link>
              </li>
            </ul>
          </aside>

        </div>
      </section>

    </main>
  );
}
